import * as Y from 'yjs';
import type { Item } from './schema';
import { rectsIntersect, rotatedBounds, type Rect } from './geometry';
import { SpatialIndex } from './SpatialIndex';

/**
 * Read-side cache over the Yjs `items` map: plain Item objects, a z-sorted list,
 * a spatial index, and a subscription API for useSyncExternalStore.
 * Item objects are replaced (never mutated) when they change, so reference equality works.
 */
export class ItemIndex {
  private cache = new Map<string, Item>();
  private groups = new Map<string, Set<string>>();
  private layerRank = new Map<string, number>();
  private sorted: Item[] | null = null;
  private version = 0;
  private listeners = new Set<() => void>();
  readonly spatial = new SpatialIndex();

  constructor(private readonly items: Y.Map<Y.Map<unknown>>) {
    items.forEach((_, id) => this.load(id));
    items.observeDeep(this.onEvents);
  }

  private onEvents = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    const touched = new Set<string>();
    for (const ev of events) {
      if (ev.target === this.items) {
        for (const key of ev.keys.keys()) touched.add(key);
      } else {
        const id = ev.path[0];
        if (typeof id === 'string') touched.add(id);
      }
    }
    for (const id of touched) this.load(id);
    if (touched.size) this.bump();
  };

  private load(id: string): void {
    const prev = this.cache.get(id);
    if (prev?.groupId) this.groups.get(prev.groupId)?.delete(id);
    const y = this.items.get(id);
    if (!y) {
      this.cache.delete(id);
      this.spatial.remove(id);
      return;
    }
    const item = { ...(y.toJSON() as Omit<Item, 'id'>), id } as Item;
    this.cache.set(id, item);
    this.spatial.set(id, rotatedBounds(item));
    if (item.groupId) {
      let g = this.groups.get(item.groupId);
      if (!g) {
        g = new Set();
        this.groups.set(item.groupId, g);
      }
      g.add(id);
    }
  }

  /** Stacking rank per layer id (unknown/default = 0). Re-sorts and notifies. */
  setLayerRanks(ranks: Map<string, number>): void {
    this.layerRank = ranks;
    this.bump();
  }

  private rankOf(item: Item): number {
    return item.layerId ? (this.layerRank.get(item.layerId) ?? 0) : 0;
  }

  /** Layer order first, then z. */
  readonly compare = (a: Item, b: Item): number => {
    const ra = this.rankOf(a);
    const rb = this.rankOf(b);
    return ra !== rb ? ra - rb : byZ(a, b);
  };

  /** Ids of the items in a group (empty when unknown). */
  membersOf(groupId: string): ReadonlySet<string> {
    return this.groups.get(groupId) ?? EMPTY;
  }

  private bump(): void {
    this.version++;
    this.sorted = null;
    for (const l of this.listeners) l();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getVersion = (): number => this.version;

  get(id: string): Item | undefined {
    return this.cache.get(id);
  }

  has(id: string): boolean {
    return this.cache.has(id);
  }

  get size(): number {
    return this.cache.size;
  }

  /** All items sorted by z (bottom first). Cached until the next change. */
  all(): Item[] {
    if (!this.sorted) {
      this.sorted = [...this.cache.values()].sort(this.compare);
    }
    return this.sorted;
  }

  /** Items whose rotated bounds intersect the rect, sorted by z. */
  query(rect: Rect): Item[] {
    const out: Item[] = [];
    for (const id of this.spatial.query(rect)) {
      const item = this.cache.get(id);
      if (item && rectsIntersect(rotatedBounds(item), rect)) out.push(item);
    }
    return out.sort(this.compare);
  }

  destroy(): void {
    this.items.unobserveDeep(this.onEvents);
    this.listeners.clear();
  }
}

export const byZ = (a: Item, b: Item): number => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0);

const EMPTY: ReadonlySet<string> = new Set();
